import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { blogs } from '../../portfolio';
import './BlogPost.css';

const BlogPost = () => {
    const { slug } = useParams();
    const activeBlog = blogs.find((b) => b.slug === slug);

    if (!activeBlog) {
        return <Navigate to='/blogs' replace />;
    }

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    return (
        <div className="split-layout">
            {/* LEFT COLUMN: List of blogs */}
            <div className="split-sidebar" style={{ width: 'min(400px, 100%)', display: 'flex', flexDirection: 'column' }}>
                <h3 className="section-title" style={{ marginBottom: '24px' }}>Writing</h3>
                <div className="writing-list" style={{ gap: '0', overflowY: 'auto' }}>
                {blogs.map((blog, idx) => {
                    const date = new Date(blog.publishedDate);
                    const yearStr = date.getFullYear().toString();
                    const dateStr = date.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
                    const isActive = blog.slug === slug;

                    return (
                    <Link 
                        key={idx} 
                        to={`/blogs/${blog.slug}`} 
                        className="writing-item" 
                        style={{
                            padding: '16px 12px',
                            borderRadius: '6px',
                            transition: 'background 0.2s',
                            alignItems: 'flex-start',
                            background: isActive ? 'var(--surface-hover)' : 'transparent',
                            color: isActive ? 'var(--text-1)' : 'inherit',
                        }}
                    >
                        <div style={{ flex: 1, paddingRight: '16px', minWidth: 0 }}>
                            <div className="writing-title" style={{ 
                                fontWeight: isActive ? '600' : '400',
                                color: isActive ? 'var(--text-1)' : 'var(--text-2)',
                                marginBottom: '4px' 
                            }}>{blog.title}</div>
                            <div className="writing-date" style={{ opacity: isActive ? 0.9 : 0.6 }}>{dateStr}</div>
                        </div>
                        <div className="writing-date" style={{ opacity: 0.6 }}>{yearStr}</div>
                    </Link>
                    );
                })}
                </div>
            </div>

            {/* RIGHT COLUMN: Blog content */}
            <div className="split-content blog-post-article">
                <style dangerouslySetInnerHTML={{__html: `
                    .blog-post-article h1, .blog-post-article h2, .blog-post-article h3 {
                        color: var(--text-1);
                        font-weight: 600;
                        margin-top: 32px;
                        margin-bottom: 16px;
                        line-height: 1.3;
                    }
                    .blog-post-article h1 { font-size: 32px; margin-top: 0; }
                    .blog-post-article h2 { font-size: 24px; }
                    .blog-post-article h3 { font-size: 20px; }
                    
                    .blog-post-article .blog-meta {
                        color: var(--text-3);
                        font-family: var(--mono);
                        font-size: 14px;
                        margin-bottom: 40px;
                        display: flex;
                        gap: 16px;
                    }

                    .blog-post-article p {
                        font-size: 15px;
                        line-height: 1.8;
                        color: var(--text-2);
                        margin-bottom: 24px;
                    }

                    .blog-post-article img {
                        max-width: 100%;
                        border-radius: var(--radius);
                        margin: 32px 0;
                        border: 1px solid var(--border);
                    }

                    .blog-post-article pre {
                        background: var(--surface-hover);
                        padding: 16px;
                        border-radius: var(--radius-sm);
                        overflow-x: auto;
                        font-family: var(--mono);
                        font-size: 13px;
                        margin-bottom: 24px;
                        border: 1px solid var(--border);
                        color: var(--text-2);
                    }
                    .blog-post-article code {
                        font-family: var(--mono);
                        background: var(--surface-hover);
                        padding: 2px 6px;
                        border-radius: 4px;
                        font-size: 0.9em;
                    }
                    .blog-post-article pre code {
                        background: transparent;
                        padding: 0;
                    }

                    .blog-post-article ul, .blog-post-article ol {
                        margin-bottom: 24px;
                        padding-left: 24px;
                        color: var(--text-2);
                    }
                    .blog-post-article li {
                        margin-bottom: 8px;
                        line-height: 1.7;
                    }
                `}} />

                <h1>{activeBlog.title}</h1>
                <div className="blog-meta">
                    <span>{formatDate(activeBlog.publishedDate)}</span>
                    {activeBlog.readTime && <span>{activeBlog.readTime}</span>}
                </div>
                
                <ReactMarkdown>{activeBlog.content}</ReactMarkdown>
            </div>
        </div>
    );
};

export default BlogPost;
